import React, {useState} from "react";
import {Form, Input, Modal} from "antd";
import {useForm} from "antd/es/form/Form";
import {useTranslate} from "../../../hooks/translate.hook";
import {
    HealthCheckerIssueDetails,
    HealthCheckerIssueResolveRequest,
} from "../../../models/health-checker/health-checker";
import {healthCheckerService} from "../../../services/health-checker/health-checker.service";
import {notifications} from "../../../services/notifications/notifications";

interface IssueResolveModalProps {
    issue: HealthCheckerIssueDetails;
    onCancel: () => void;
    onComplete: () => Promise<void> | void;
}

export const IssueResolveModal: React.FC<IssueResolveModalProps> = ({issue, onCancel, onComplete}) => {
    const t = useTranslate();
    const [form] = useForm<HealthCheckerIssueResolveRequest>();
    const [loading, setLoading] = useState<boolean>(false);

    const submit = async (values: HealthCheckerIssueResolveRequest) => {
        let completed = false;

        try {
            setLoading(true);
            await healthCheckerService.resolveIssue(issue.id, values);
            await onComplete();
            notifications.successfully();
            completed = true;
        } catch (error: any) {
            form.setFields(error.response?.data?.fields || []);
        } finally {
            setLoading(false);
        }

        if (completed) {
            onCancel();
        }
    };

    return (
        <Modal
            title={t("HEALTH-CENTER.ISSUE.RESOLVE-TITLE", {
                id: issue.id,
                defaultValue: "Resolve issue #{{id}}",
            })}
            open
            destroyOnClose
            confirmLoading={loading}
            okText={t("HEALTH-CENTER.ISSUE.RESOLVE", {defaultValue: "Resolve"})}
            cancelText={t("CANCEL", {defaultValue: "Cancel"})}
            cancelButtonProps={{disabled: loading}}
            onCancel={onCancel}
            onOk={() => form.submit()}
        >
            <Form<HealthCheckerIssueResolveRequest>
                form={form}
                layout="vertical"
                disabled={loading}
                onFinish={submit}
            >
                <Form.Item
                    label={t("HEALTH-CENTER.ISSUE.RESOLVE-COMMENT", {defaultValue: "Resolution comment"})}
                    name="comment"
                    rules={[{
                        required: true,
                        whitespace: true,
                        message: t("FROM.ERROR.PLEASE_ENTER_VALUE", {defaultValue: "Please enter a value"}),
                    }]}
                >
                    <Input.TextArea
                        autoFocus
                        autoSize={{minRows: 4, maxRows: 8}}
                        placeholder={t("HEALTH-CENTER.ISSUE.RESOLVE-COMMENT-PLACEHOLDER", {
                            defaultValue: "Describe how the issue was resolved",
                        })}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
